import { observer } from 'mobx-react';
import React, { useEffect } from 'react';

import { Box, Button } from '@mui/material';
import SearchModelSelect from '@common/components/Form/SearchSelect/SearchModelSelect';
import { Field as FormikField, FieldProps, useFormikContext } from 'formik';

import RBAC, { checkRole } from '@common/components/RBAC';
import { IChatGroupsModel } from '@common/models/ChatGroups/ChatGroups';
import { IChatProgramsModel } from '@common/models/ChatPrograms/ChatPrograms';

import { useConfig } from '../config';
import { getChatRules } from '../config/ChatRules';
import ChatRules from './Rules';
import * as Styled from './styled';

interface FormValues {
	program: { id: number }[];
	group: { id: number }[];
}

interface ChatTitleProps {
	chatProgramsModel: IChatProgramsModel;
	chatGroupsModel: IChatGroupsModel;
	onGroupsChange: (ids: number[]) => void;
	onProgramsChange: (ids: number[]) => void;
	openMassMailingModal: (arg?: any) => void;
}

interface SearchModelSelectFieldProps {
	name: string;
	label: string;
	modelInstance: IChatGroupsModel | IChatProgramsModel;
}

function SearchModelSelectField({ name, label, modelInstance }: SearchModelSelectFieldProps) {
	return (
		<FormikField name={name}>
			{({ field, form }: FieldProps) => (
				<SearchModelSelect
					label={label}
					itemLabelKey="name"
					modelInstance={modelInstance}
					multiple
					disableClearable={false}
					value={field.value}
					onChange={(value) => form.setFieldValue(name, value)}
				/>
			)}
		</FormikField>
	);
}

function ChatTitle({
	chatProgramsModel,
	chatGroupsModel,
	onGroupsChange,
	onProgramsChange,
	openMassMailingModal,
}: ChatTitleProps) {
	const showChatRules = useConfig(getChatRules);
	const { values } = useFormikContext<FormValues>();
	const isCurator = checkRole('Curator');

	useEffect(() => {
		onProgramsChange(values.program?.map((program) => program.id));
	}, [values.program, onProgramsChange]);

	useEffect(() => {
		onGroupsChange(values.group?.map((group) => group.id));
	}, [values.group, onGroupsChange]);

	return (
		<Box display="flex" flexDirection="column" gap="8px">
			<Box display="flex" justifyContent="space-between" alignItems="center" paddingRight="16px">
				<Styled.Wrap>
					Chat{' '}
					{showChatRules && (
						<RBAC grantedOnly granted={['Student', 'Teacher', 'Curator']}>
							<ChatRules />
						</RBAC>
					)}
				</Styled.Wrap>
				{isCurator && (
					<Button variant="contained" onClick={() => openMassMailingModal()}>
						Mass mailing
					</Button>
				)}
			</Box>
			{isCurator && (
				<Box display="flex" flexDirection="column" gap="16px" padding="0 16px 8px">
					<SearchModelSelectField
						name="program"
						label="Program"
						modelInstance={chatProgramsModel}
					/>
					<SearchModelSelectField
						name="group"
						label="Group"
						modelInstance={chatGroupsModel}
					/>
				</Box>
			)}
		</Box>
	);
}

export default observer(ChatTitle);
